/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { login } from '../core/_requests'
import { toAbsoluteUrl } from '_metronic/helpers'

import { useAppStore } from 'Store/App'
import { useAuthSessionStore } from 'Store/AuthSession'
import { useAuthStore } from 'Store/Auth'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'
import { useLocation, useNavigate } from 'react-router-dom'

const loginSchema = Yup.object().shape({
	email: Yup.string()
		.email('Wrong email format')
		.min(3, 'Minimum 3 symbols')
		.max(50, 'Maximum 50 symbols')
		.required('Email is required'),
	password: Yup.string()
		.min(3, 'Minimum 3 symbols')
		.max(50, 'Maximum 50 symbols')
		.required('Password is required'),
})

const initialValues = {
	email: process.env.REACT_APP_USERNAME ?? '',
	password: process.env.REACT_APP_PASSWORD ?? '',
	isRemember: false,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
	const navigate = useNavigate()
	const location = useLocation()
	const [loading, setLoading] = useState(false)
	const [showPassword, setShowPassword] = useState(false)

	const { setCookieAccessToken, setCookieRefreshToken } = useAuthStore(
		state => ({
			setCookieAccessToken: state.setCookieAccessToken,
			setCookieRefreshToken: state.setCookieRefreshToken,
		}),
		shallow
	)
	const { setSessionAccessToken } = useAuthSessionStore(
		state => ({
			setSessionAccessToken: state.setAccessToken,
		}),
		shallow
	)
	// @ts-ignore
	const prevPage = location?.state?.prevPage
	const setPrevPage = useAppStore(state => state.setPrevPage, shallow)
	const setHasFetchProfile = useAppStore(state => state.setHasFetchProfile, shallow)

	const formik = useFormik({
		initialValues,
		validationSchema: loginSchema,
		onSubmit: async (values, { setStatus, setSubmitting }) => {
			setLoading(true)
			try {
				const { data: auth } = await login(values.email, values.password)
				const { accessToken, refreshToken } = auth

				if (values.isRemember) {
					setCookieAccessToken(accessToken)
					setCookieRefreshToken(refreshToken)
				} else {
					setSessionAccessToken(accessToken)
				}

				setHasFetchProfile(false)
			} catch (error: any) {
				if (
					error?.response?.data?.message?.toString() === 'Supplier is deleted' ||
					error?.response?.data?.message?.toString() === 'ผู้ใช้งานถูกลบแล้ว'
				) {
					Swal.fire({
						title: 'ไม่สามารถเชื่อมต่อกับระบบได้',
						text: 'ผู้ใช้งานของคุณถูกลบแล้ว',
						icon: 'error',
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
						confirmButtonText: 'ตกลง',
					}).then(() => {
						setSubmitting(false)
						setLoading(false)
					})
					return
				} else {
					setStatus(
						error?.response?.data?.message?.toString() ?? 'ไม่สามารถลงชื่อเข้าใช้งานได้ในขณะนี้'
					)
					setSubmitting(false)
					setLoading(false)
				}
			}
		},
	})

	useEffect(() => {
		if (prevPage) {
			setPrevPage(prevPage)
		} else {
			setPrevPage(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prevPage])

	// Handle account deletion from SSO
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search)
		const deleted = searchParams.get('deleted')

		if (deleted && deleted === 'true') {
			Swal.fire({
				title: 'ไม่สามารถเชื่อมต่อกับระบบได้',
				text: 'ผู้ใช้งานของคุณถูกลบแล้ว',
				icon: 'error',
				customClass: {
					confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
				},
				confirmButtonText: 'ตกลง',
			}).then(() => {
				useAuthStore.getState().clearState()
				useAuthSessionStore.getState().clearState()
				navigate('/auth', { replace: true })
			})
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location])

	return (
		<form
			className="form w-100"
			onSubmit={e => {
				e.preventDefault()
				formik.handleSubmit(e)
			}}
			noValidate
			id="kt_login_signin_form">
			{/* begin::Heading */}
			<div className="text-center mb-10">
				<h1 className="text-dark mb-3">Sign In to Kumopack Supplier</h1>
				<div className="text-kumopack-grey-600 fw-bold fs-4">
					New Here?{' '}
					<Link to="/auth/registration" className="text-kumopack-primary-700 fw-bolder">
						Create an Account
					</Link>
				</div>
			</div>
			{/* begin::Heading */}

			{formik.status ? (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">{formik.status}</div>
				</div>
			) : (
				<></>
				// <div className="mb-10 bg-light-info p-8 rounded">
				// 	<div className="text-info">
				// 		Use account <strong>admin@demo.com</strong> and password <strong>demo</strong> to
				// 		continue.
				// 	</div>
				// </div>
			)}

			{/* begin::Form group */}
			<div className="fv-row mb-10">
				<label className="form-label fs-6 fw-bolder text-dark">Email</label>
				<input
					placeholder="Email"
					{...formik.getFieldProps('email')}
					className={clsx(
						'form-control form-control-lg form-control-solid',
						{ 'is-invalid': formik.touched.email && formik.errors.email },
						{
							'is-valid': formik.touched.email && !formik.errors.email,
						}
					)}
					type="email"
					name="email"
					autoComplete="off"
				/>
				{formik.touched.email && formik.errors.email && (
					<div className="fv-plugins-message-container">
						<span role="alert">{formik.errors.email}</span>
					</div>
				)}
			</div>
			{/* end::Form group */}

			{/* begin::Form group */}
			<div className="fv-row mb-10">
				<div className="d-flex justify-content-between mt-n5">
					<div className="d-flex flex-stack mb-2">
						{/* begin::Label */}
						<label className="form-label fw-bolder text-dark fs-6 mb-0">Password</label>
						{/* end::Label */}
						{/* begin::Link */}
						<Link
							to="/auth/forgot-password"
							className="text-kumopack-primary-700 fs-6 fw-bolder"
							style={{ marginLeft: '5px' }}>
							Forgot Password ?
						</Link>
						{/* end::Link */}
					</div>
				</div>
				<div className="position-relative">
					<input
						type={showPassword ? 'text' : 'password'}
						autoComplete="off"
						{...formik.getFieldProps('password')}
						className={clsx(
							'form-control form-control-lg form-control-solid',
							{
								'is-invalid': formik.touched.password && formik.errors.password,
							},
							{
								'is-valid': formik.touched.password && !formik.errors.password,
							}
						)}
					/>
					<span
						className="btn btn-sm btn-icon btn-active-secondary position-absolute translate-middle top-50 end-0 me-n2"
						onClick={() => setShowPassword(prevState => !prevState)}>
						<img
							src={
								showPassword
									? '/media/icons/kumopack/eye-grey.svg'
									: '/media/icons/kumopack/eye-off-grey.svg'
							}
							alt="Eye Off Grey"
							width={17}
							style={{ userSelect: 'none', pointerEvents: 'none' }}
						/>
					</span>
				</div>
				{formik.touched.password && formik.errors.password && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">
							<span role="alert">{formik.errors.password}</span>
						</div>
					</div>
				)}
				<div className="row pt-5">
					<div className="col-7">
						{/* <div className="row">
							<div className="col-2">
								<input
									id="isRemember"
									name="isRemember"
									className={clsx('form-check-input', {
										'bg-kumopack-primary-600 border-kumopack-base-white': formik.values.isRemember,
										'border-kumopack-gray-700': !formik.values.isRemember,
									})}
									style={{ backgroundSize: formik.values.isRemember ? 'initial' : 'contain' }}
									type="checkbox"
									checked={formik.values.isRemember}
									onChange={formik.handleChange}
								/>
							</div>
							<div className="col-10">
								<label className="fs-6 cursor-pointer" htmlFor="isRemember">
									Remember Me
								</label>
							</div>
						</div> */}
					</div>
					<div className="col-5" style={{ textAlign: 'right' }}>
						{/* begin::Link */}
						<Link to="/auth/forgot-password" className="text-kumopack-primary-700 fs-6 fw-bold">
							Forgot Password
						</Link>
						{/* end::Link */}
					</div>
				</div>
			</div>
			{/* end::Form group */}

			{/* begin::Action */}
			<div className="text-center">
				<button
					type="submit"
					id="kt_sign_in_submit"
					className="btn btn-lg btn-kumopack-primary-600 text-kumopack-base-white w-100 mb-5"
					disabled={formik.isSubmitting || !formik.isValid}>
					{!loading && <span className="indicator-label">Sign In</span>}
					{loading && (
						<span
							className="indicator-progress text-kumopack-base-white"
							style={{ display: 'block' }}>
							Please wait...
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					)}
				</button>

				{/* begin::Separator */}
				<div className="text-center text-muted text-uppercase fw-bolder mb-5">or</div>
				{/* end::Separator */}

				{/* begin::Google link */}
				<a
					href={process.env.REACT_APP_GOOGLE_AUTH_URL ?? ''}
					className="btn btn-flex flex-center btn-light btn-lg w-100 mb-5">
					<img
						alt="Logo"
						src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
						className="h-20px me-3"
						style={{ userSelect: 'none', pointerEvents: 'none' }}
					/>
					Continue with Google
				</a>
				{/* end::Google link */}

				{/* begin::Google link */}
				{/* <a href="#" className="btn btn-flex flex-center btn-light btn-lg w-100 mb-5">
					<img
						alt="Logo"
						src={toAbsoluteUrl('/media/svg/brand-logos/facebook-4.svg')}
						className="h-20px me-3"
						style={{ userSelect: 'none', pointerEvents: 'none' }}
					/>
					Continue with Facebook
				</a> */}
				{/* end::Google link */}

				{/* begin::Google link */}
				{/* <a href="#" className="btn btn-flex flex-center btn-light btn-lg w-100">
					<img
						alt="Logo"
						src={toAbsoluteUrl('/media/svg/brand-logos/apple-black.svg')}
						className="h-20px me-3"
						style={{ userSelect: 'none', pointerEvents: 'none' }}
					/>
					Continue with Apple
				</a> */}
				{/* end::Google link */}
			</div>
			{/* end::Action */}
		</form>
	)
}
